import type { Branded } from "@@/utilities/branded";
import { toObjectMap } from "@@/utilities/dictionary";

const sizeNames = [
    "3XS",
    "XXS",
    "XS",
    "S",
    "M",
    "ML",
    "L",
    "XL",
    "XXL",
    "3XL",
] as const;
type SizeName = (typeof sizeNames)[number];

type BasePixelSize = PixelSize;
const defaultSize = 16 as BasePixelSize;

type SizeNameMap = { [key in SizeName]: key };
const sizeNameMap = toObjectMap<SizeName>([...sizeNames]);

type SizeTheme = {
    base: number;
    inRem: {
        [key in SizeName]: RemSize;
    };
    inPixels: {
        [key in SizeName]: number;
    };
    imageSizes: {
        thumb: number;
        medium: number;
        large: number;
        closest: (width: number) => number;
    };
    pageMaxWidthDesktop: PixelSize;
};

type RemSize = Branded<number, "size_in_rem">;
const remSize = (value: number): RemSize => value as RemSize;
type EmSize = Branded<number, "size_in_em">;
const emSize = (value: number): EmSize => value as EmSize;
type PixelSize = Branded<number, "size_in_pixels">;
const createSizeTheme = (baseSize: BasePixelSize = defaultSize): SizeTheme => {
    const remSizes: Record<SizeName, RemSize> = {
        "3XS": 0.5 as RemSize, // 8px
        XXS: 0.625 as RemSize, // 10px
        XS: 0.75 as RemSize, // 12px
        S: 0.875 as RemSize, // 14px
        M: 1 as RemSize, // 16px
        ML: 1.125 as RemSize, // 18px
        L: 1.25 as RemSize, // 20px
        XL: 1.5 as RemSize, // 24px
        XXL: 2 as RemSize, // 32px
        "3XL": 2.5 as RemSize, // 40px
    };
    const pixelSizes: Record<SizeName, PixelSize> = (
        Object.keys(remSizes) as (keyof Record<SizeName, PixelSize>)[]
    ).reduce(
        (agg, key) => {
            agg[key] = (remSizes[key] * baseSize) as PixelSize;
            return agg;
        },
        {} as Record<SizeName, PixelSize>,
    );

    const calcLarge = (): PixelSize => {
        const portraitWidth = window
            ? window.innerWidth < window.innerHeight
                ? window.innerWidth
                : window.innerHeight
            : 1024; // if no window (like in node env)
        const retinaWidth = portraitWidth * 2;
        const large =
            retinaWidth <= 256
                ? 256
                : retinaWidth <= 384
                  ? 384
                  : retinaWidth <= 512
                    ? 512
                    : retinaWidth <= 768
                      ? 768
                      : 1024;

        return large as PixelSize;
    };

    type ImageSizeName =
        | "thumb"
        | "small"
        | "smallMedium"
        | "medium"
        | "mediumLarge"
        | "large";
    const imageSizes: Record<ImageSizeName, PixelSize> = {
        thumb: 128 as PixelSize,
        small: 256 as PixelSize,
        smallMedium: 384 as PixelSize,
        medium: 512 as PixelSize,
        mediumLarge: 768 as PixelSize,
        large: calcLarge(),
    };

    const closest = (width: number): number => {
        const retinaWidth = width * 2;
        const closest: number = (
            retinaWidth <= imageSizes.thumb
                ? imageSizes.thumb
                : retinaWidth <= imageSizes.small
                  ? imageSizes.small
                  : retinaWidth <= imageSizes.smallMedium
                    ? imageSizes.smallMedium
                    : retinaWidth <= imageSizes.medium
                      ? imageSizes.medium
                      : retinaWidth <= imageSizes.mediumLarge
                        ? imageSizes.mediumLarge
                        : imageSizes.large
        ) satisfies number;
        return closest;
    };

    return {
        base: baseSize,
        inRem: remSizes,
        pageMaxWidthDesktop: 1024 as PixelSize,
        inPixels: pixelSizes,
        imageSizes: {
            ...imageSizes,
            closest,
        },
    };
};

export {
    createSizeTheme,
    defaultSize,
    emSize,
    remSize,
    sizeNameMap,
    sizeNames,
};
export type { EmSize, PixelSize, RemSize, SizeName, SizeNameMap, SizeTheme };
